import { httpService } from './http.service';
import { API_ENDPOINTS } from '../config/api.config';

class ResponseService {
  /**
   * Créer des réponses selon le modèle UserResponseCreate
   * @param {Array} responses - Array des réponses/sélections
   * @returns {Promise<Array>} - Réponses créées
   */
  async createResponses(responses) {
    try {
      console.log('Création des réponses:', responses);
      console.log('URL de l\'API des réponses:', API_ENDPOINTS.RESPONSES.CREATE());
      if (!Array.isArray(responses) || responses.length === 0) {
        console.log('Aucune réponse à créer.');
        return [];
      }

      console.log(`Envoi de ${responses.length} requêtes de création de réponse...`);
      
      // L'API attend un objet à la fois, pas une liste.
      // Nous devons donc faire un appel POST pour chaque réponse individuellement.
      const createPromises = responses.map(singleResponse => 
        httpService.post(API_ENDPOINTS.RESPONSES.CREATE(), singleResponse)
      );

      const createdResponses = await Promise.all(createPromises);
      
      console.log('Réponses créées avec succès:', createdResponses);
      return createdResponses;
    } catch (error) {
      console.error('Erreur lors de la création des réponses:', error);
      throw error;
    }
  }

  /**
   * Soumettre les réponses/sélections pour une demande
   * @param {number} demandeId - ID de la demande
   * @param {Array} responses - Array des réponses/sélections
   * @returns {Promise<Object>} - Réponse de l'API
   */
  async submitResponses(demandeId, responses) {
    try {
      console.log('Soumission des réponses pour la demande:', demandeId, responses);
      
      // Ajouter l'ID de la demande à chaque réponse
      const responsesWithDemande = responses.map(response => ({
        ...response,
        demande: demandeId
      }));
      
      console.log('Réponses avec demande ID:', responsesWithDemande);
      console.log('URL de l\'API des réponses:', API_ENDPOINTS.RESPONSES.CREATE());
      
      const response = await httpService.post(
        API_ENDPOINTS.RESPONSES.CREATE(),
        responsesWithDemande
      );
      
      console.log('Réponses soumises avec succès:', response);
      return response;
    } catch (error) {
      console.error('Erreur lors de la soumission des réponses:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour les réponses avec l'ID de la demande
   * @param {Array} createdResponses - Le tableau des réponses créées précédemment.
   * @param {string} demandeId - L'ID de la demande à laquelle lier les réponses.
   * @returns {Promise<Object>} - Réponse de l'API
   */
  async linkResponsesToDemande(createdResponses, demandeId) {
    try {
      console.log(`Liaison de ${createdResponses.length} réponses à la demande ID:`, demandeId);
      
      // Mettre à jour chaque réponse individuellement
      const updatePromises = createdResponses.map(response => {
        return httpService.put(
          API_ENDPOINTS.RESPONSES.UPDATE(response.id),
          // { demande: response.demande }
          // L'API attend l'objet complet pour la mise à jour.
          // Nous ajoutons l'ID de la demande à l'objet réponse existant.
          { ...response, demande: demandeId }
        );
      });
      
      const results = await Promise.all(updatePromises);
      console.log('Réponses mises à jour avec succès:', results);
      return { success: true, data: results };
    } catch (error) {
      console.error('Erreur lors de la mise à jour des réponses:', error);
      throw error;
    }
  }

  /**
   * Récupérer les réponses d'une demande
   * @param {number} demandeId - ID de la demande
   * @returns {Promise<Array>} - Liste des réponses
   */
  async getResponses(demandeId) {
    try {
      console.log('Récupération des réponses pour la demande:', demandeId);
      
      // const response = await httpService.get(API_ENDPOINTS.RESPONSES.LIST(demandeId));
      const response = await httpService.get(API_ENDPOINTS.RESPONSES.LIST_ALL());
      console.log('Réponses récupérées:', response);
      
      // S'assurer que la réponse est un tableau
      const responses = Array.isArray(response.results) ? response.results : Array.isArray(response) ? response : [];
      
      // Valider que chaque réponse correspond au modèle BeResponse
      const validatedResponses = responses.map(response => ({
        id: response.id,
        input_field_name: response.input_field_name,
        content_type_name: response.content_type_name,
        value: response.value,
        submitted_at: response.submitted_at
      }));
      
      console.log('Réponses validées selon BeResponse:', validatedResponses);
      return validatedResponses;
    } catch (error) {
      // Si l'endpoint n'existe pas (404), retourner un tableau vide
      if (error.message && error.message.includes('404')) {
        console.warn('Endpoint des réponses non disponible (404), retour d\'un tableau vide');
        return [];
      }
      console.error('Erreur lors de la récupération des réponses:', error);
      throw error;
    }
  }

  /**
   * Formater les sélections en réponses selon le modèle BeResponse
   * @param {Array} selectedElements - Éléments sélectionnés (format useFormData)
   * @returns {Array} - Array des réponses formatées selon BeResponse
   */
  formatSelectionsAsResponses(selectedElements) {
    const responses = [];
    
    console.log('Formatage des sélections en réponses:', selectedElements);
    
    // selectedElements est maintenant un tableau d'éléments avec stepId, sectionId, etc.
    selectedElements.forEach((element, index) => {
      const { stepId, sectionId, id: contentId, ...elementData } = element;
      
      console.log(`Traitement de l'élément ${index}:`, element);
      
      // Traiter les différents types de contenu selon les données disponibles
      const { contentType, selections, inputs, otherChoices, textContent, title, value } = elementData;
      
      // Si c'est un produit sélectionné (selections) ou un choix (choices)
      if (contentType === 'PRODUCTS' || (selections && selections.length > 0)) {
        console.log(`Traitement des produits pour le contenu ${contentId}:`, selections);
        const products = selections || [element];
        products.forEach((selection) => {
          // Pour les produits, l'input field est souvent le contenu lui-même.
          responses.push({
            // input_field: element.input_id || contentId,
            content: contentId,
            value: selection.id ? selection.id.toString() : selection.toString()
          });
        });
      }
      
      // Si c'est un input
      if (contentType === 'INPUT' || (inputs && Object.keys(inputs).length > 0)) {
        const inputData = inputs || { [contentId]: value };
        Object.entries(inputData).forEach(([inputId, inputValue]) => {
          if (inputValue !== null && inputValue !== undefined && inputValue !== '') {
            responses.push({
              // input_field: inputId, // Ici, inputId est la clé, ce qui est correct.
              content: contentId,
              value: inputValue.toString()
            });
          }
        });
      }
      
      // Si c'est un autre choix

      if (contentType === 'OTHER_CHOICES') {
        // On s'attend à ce que les données soient dans la propriété `inputs` de l'élément,
        // comme pour le contentType 'INPUT'.
        if (inputs && Object.keys(inputs).length > 0) {
          Object.entries(inputs).forEach(([inputId, inputValue]) => {
            if (inputValue !== null && inputValue !== undefined && inputValue !== '') {
              // Chaque champ de saisie devient une réponse distincte liée au même contenu.
              responses.push({ content: contentId, value: inputValue.toString() });
            }
          });
        }
      }

      // Si c'est un choix de type CHOICES
      if (contentType === 'CHOICES' && elementData.choices && elementData.choices.length > 0) {
        const choices = elementData.choices;
        choices.forEach((choice) => {
          responses.push({
            content: contentId,
            value: choice.id ? choice.id.toString() : choice.toString()
          });
        });
      }
      
      // Si c'est du texte
      if (contentType === 'TEXT' || (textContent && textContent.trim() !== '') || (value && typeof value === 'string')) {
        const textValue = textContent || value || title;
        if (textValue && textValue.trim() !== '') {
          responses.push({
            // Pour le texte, l'input field est souvent le contenu lui-même.
            // input_field: element.input_id || contentId,
            content: contentId,
            value: textValue.toString()
          });
        }
      }
      
      // Si aucun type spécifique n'est défini mais qu'il y a des données
      if (!contentType && !selections && !inputs && !otherChoices && !textContent) {
        // Essayer de déterminer le type basé sur les données disponibles
        if (value !== undefined && value !== null) {
          responses.push({
            // input_field: element.input_id || contentId,
            content: contentId,
            value: value.toString()
          });
        }
      }
    });
    
    console.log('Réponses formatées selon le modèle BeResponse:', responses);
    return responses;
  }
}

export const responseService = new ResponseService();